import React, { useState } from 'react';
import {
  format,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  isToday,
  parseISO,
} from 'date-fns';
import { ChevronLeft, ChevronRight, Plus, Droplets, Zap, Sparkles, Scale, Camera, FileText } from 'lucide-react';
import { Entry, EntryType } from '../types';

interface CalendarViewProps {
  entries: Entry[];
  onSelectDate: (date: Date) => void;
  onAddEntryForDate: (date: Date) => void;
  onSelectEntry: (entry: Entry) => void;
}

const getCategoryIcon = (type: EntryType) => {
  switch (type) {
    case 'watering':
      return <Droplets size={12} color="#38bdf8" />;
    case 'feeding':
      return <Sparkles size={12} color="#a855f7" />;
    case 'light':
      return <Zap size={12} color="#f59e0b" />;
    case 'yield':
      return <Scale size={12} color="#10b981" />;
    case 'photo':
      return <Camera size={12} color="#ec4899" />;
    default:
      return <FileText size={12} color="#94a3b8" />;
  }
};

export const CalendarView: React.FC<CalendarViewProps> = ({
  entries,
  onSelectDate,
  onAddEntryForDate,
  onSelectEntry,
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState<Date>(new Date());

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(monthStart);
  const startDate = startOfWeek(monthStart);
  const endDate = endOfWeek(monthEnd);

  const days = eachDayOfInterval({ start: startDate, end: endDate });

  const getDayEntries = (day: Date) => {
    return entries.filter((e) => {
      try {
        return isSameDay(parseISO(e.entry_date), day);
      } catch {
        return false;
      }
    });
  };

  const selectedDayEntries = getDayEntries(selectedDay);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Calendar Header */}
      <div className="glass-panel" style={{ padding: '1.25rem 1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <div>
            <h2 style={{ fontSize: '1.35rem', color: 'var(--text-main)' }}>
              {format(currentMonth, 'MMMM yyyy')}
            </h2>
            <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
              {entries.length} total events logged across schedule
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setCurrentMonth(new Date())}
            >
              Today
            </button>
            <button
              className="btn btn-secondary btn-icon"
              onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
              title="Previous Month"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              className="btn btn-secondary btn-icon"
              onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
              title="Next Month"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="calendar-grid">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
            <div key={d} className="calendar-header-day">
              {d}
            </div>
          ))}

          {days.map((day, idx) => {
            const dayEntries = getDayEntries(day);
            const isCurrMonth = isSameMonth(day, monthStart);
            const isSelected = isSameDay(day, selectedDay);
            const isCurrentDay = isToday(day);

            return (
              <div
                key={idx}
                className={`calendar-day-cell ${!isCurrMonth ? 'is-other-month' : ''} ${isCurrentDay ? 'is-today' : ''}`}
                style={{
                  border: isSelected ? '1px solid var(--accent-emerald)' : undefined,
                  background: isSelected ? 'rgba(16, 185, 129, 0.12)' : undefined,
                }}
                onClick={() => {
                  setSelectedDay(day);
                  onSelectDate(day);
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span className="calendar-day-num">{format(day, 'd')}</span>
                  {dayEntries.length > 0 && (
                    <span
                      style={{
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        background: 'rgba(16, 185, 129, 0.2)',
                        color: 'var(--accent-emerald-light)',
                        borderRadius: '50%',
                        width: 16,
                        height: 16,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {dayEntries.length}
                    </span>
                  )}
                </div>

                {/* Dots / Tiny tags */}
                <div style={{ marginTop: 'auto', display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
                  {dayEntries.slice(0, 3).map((e) => (
                    <div
                      key={e.id}
                      title={`${e.category_type}: ${e.title}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        fontSize: '0.65rem',
                        background: 'rgba(255, 255, 255, 0.08)',
                        borderRadius: 3,
                        padding: '1px 3px',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        maxWidth: '100%',
                      }}
                    >
                      {getCategoryIcon(e.category_type)}
                      <span style={{ textOverflow: 'ellipsis', overflow: 'hidden' }}>{e.title}</span>
                    </div>
                  ))}
                  {dayEntries.length > 3 && (
                    <span style={{ fontSize: '0.65rem', color: 'var(--text-sub)' }}>
                      +{dayEntries.length - 3}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Selected Day Details Panel */}
      <div className="glass-panel" style={{ padding: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <div>
            <h3 style={{ fontSize: '1.15rem' }}>
              Entries for {format(selectedDay, 'EEEE, MMMM d, yyyy')}
            </h3>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              {selectedDayEntries.length} log(s) recorded on this date
            </p>
          </div>

          <button
            className="btn btn-primary btn-sm"
            onClick={() => onAddEntryForDate(selectedDay)}
          >
            <Plus size={14} />
            <span>Add Log for this Day</span>
          </button>
        </div>

        {selectedDayEntries.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem 1rem', color: 'var(--text-sub)' }}>
            <p>No activity logged for this date yet.</p>
            <button
              className="btn btn-secondary btn-sm"
              style={{ marginTop: '0.75rem' }}
              onClick={() => onAddEntryForDate(selectedDay)}
            >
              Record an entry now
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {selectedDayEntries.map((entry) => (
              <div
                key={entry.id}
                style={{
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 'var(--radius-md)',
                  padding: '1rem',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  transition: 'background 0.2s',
                }}
                onClick={() => onSelectEntry(entry)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      background: 'rgba(255, 255, 255, 0.05)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {getCategoryIcon(entry.category_type)}
                  </div>
                  <div>
                    <h4 style={{ fontSize: '0.95rem', fontWeight: 600 }}>{entry.title}</h4>
                    <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                      {entry.content ? entry.content.slice(0, 80) + '...' : 'No notes'}
                    </p>
                  </div>
                </div>

                <div style={{ textAlign: 'right' }}>
                  <span className="badge badge-emerald" style={{ textTransform: 'capitalize' }}>
                    {entry.category_type}
                  </span>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-sub)', marginTop: 4 }}>
                    {format(parseISO(entry.entry_date), 'h:mm a')}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
